/**
 * 大地测量基础 —— 围栏、备降点、测绘航线都要用到的几何原语。
 *
 * ## 用的是球面近似，不是椭球
 *
 * 所有距离/方位都按**正球体**（半径取 IUGG 平均半径 6371.0088 km）计算，
 * 不做 WGS84 椭球归算。误差量级（相对椭球测地线）：
 *
 * - **两点距离**：0% – 约 0.5%。赤道半径与极半径差 21.4 km / 6378 km ≈ 0.34%，
 *   用平均半径后，一公里尺度上的典型偏差在**厘米到分米**，上千公里尺度上
 *   最大约 0.2%–0.3%（例如 1000 km 上偏 2–3 km）。
 * - **方位角**：短距离（< 10 km）差异远小于 0.01°；长距离上"大圆起始方位角"
 *   与"等角航线方位角"会差很多，但那是**定义差异**，不是计算误差 ——
 *   本文件一律给大圆（great-circle）起始方位角。
 * - **面积**：球面公式与椭球公式在 10 km 见方的地块上相差约 0.1%；
 *   对"哪块地在围栏里"这种判断绰绰有余，对地籍测量不够。
 * - **局部平面投影**（{@link localPlaneAt}）：把球面按等距圆柱展开，
 *   在 10 km 尺度上长度误差 < 0.1%，超过 100 km 后开始明显（> 1%）。
 *   因此本文件所有"绕中心"的生成器都限定在小范围内使用。
 *
 * 结论：这些函数适用于航线规划、围栏、备降点、任务校验。
 * 若将来要做厘米级测绘或跨洲长航线，应换成 Vincenty/Karney 椭球算法，
 * 而不是在这个文件里逐条打补丁。
 *
 * ## 边界条件
 *
 * 每个函数都显式处理了退化输入（零长度线段、重复点、少于 3 个顶点的多边形、
 * 跨 180° 经线、极区）。处理方式写在各自的注释里 —— 有些返回"空"，有些返回
 * 一个定义好的退化值，理由随函数说明。
 */

/** IUGG 平均地球半径（米）。球面近似的基准。 */
export const EARTH_MEAN_RADIUS_M = 6371008.8;

/** 十进制度表示的位置。 */
export interface LatLon {
  readonly latDeg: number;
  readonly lonDeg: number;
  /** 可选高度（米，相对哪个基准由调用方定义）。图形算法不使用它。 */
  readonly altM?: number;
}

/** 局部平面坐标（原点由 {@link localPlaneAt} 指定，单位米）。 */
export interface LocalPoint {
  readonly eastM: number;
  readonly northM: number;
}

/** 地理包围盒。 */
export interface GeoBounds {
  readonly southDeg: number;
  readonly northDeg: number;
  /**
   * 西边界 / 东边界。
   *
   * **跨 180° 经线时 `westDeg > eastDeg`**：此时"盒子"表示的是
   * 从 `westDeg` 向东绕经 180° 到 `eastDeg` 的那一段（窄区间）。
   * 这是唯一可行的表示法 —— 若强行按数值大小填，跨经线的盒子会退化成
   * "几乎整个地球"，而那种错误在地图上看起来完全正常，极难发现。
   */
  readonly westDeg: number;
  readonly eastDeg: number;
  /** 是否跨 180° 经线（即 `westDeg > eastDeg`）。 */
  readonly crossesAntimeridian: boolean;
}

/* ───────────────────────── 内部小工具 ───────────────────────── */

/** 把角度差规整到 (-180, 180]，避免在 `sin/cos` 前出现 720° 之类的巨数。 */
function wrapDeltaDeg(deltaDeg: number): number {
  return ((deltaDeg % 360) + 540) % 360 - 180;
}

function clampUnit(value: number): number {
  return value < -1 ? -1 : value > 1 ? 1 : value;
}

function isFiniteLatLon(p: LatLon): boolean {
  return Number.isFinite(p.latDeg) && Number.isFinite(p.lonDeg);
}

/* ───────────────────────────── 距离 ───────────────────────────── */

/**
 * 大圆距离（haversine 公式），单位米。
 *
 * 用 haversine 而不是余弦定理：余弦定理在**小距离**上要算 `acos(≈1)`，
 * 浮点误差会被放大好几个数量级（相邻两个航点相距 1 m 时可能算出 0 或几十米）；
 * haversine 走 `asin(sqrt(...))`，同一个量级上误差稳定。
 *
 * 退化情形：同一位置 → 0；对跖点 → πR（地表最长距离的一半周长）；
 * 输入含 NaN 或超出 ±90/±180 的非法经纬度 → 返回 NaN（不猜，让调用方看见）。
 */
export function haversineDistanceM(a: LatLon, b: LatLon): number {
  if (!isFiniteLatLon(a) || !isFiniteLatLon(b)) return NaN;
  if (a.latDeg === b.latDeg && a.lonDeg === b.lonDeg) return 0;

  const lat1 = (a.latDeg * Math.PI) / 180;
  const lat2 = (b.latDeg * Math.PI) / 180;
  const halfDLat = (lat2 - lat1) / 2;
  const halfDLon = (wrapDeltaDeg(b.lonDeg - a.lonDeg) * Math.PI) / 360;

  const sinHalfDLat = Math.sin(halfDLat);
  const sinHalfDLon = Math.sin(halfDLon);
  const h = sinHalfDLat * sinHalfDLat + Math.cos(lat1) * Math.cos(lat2) * sinHalfDLon * sinHalfDLon;
  // h 在理论上不会超过 1，但浮点舍入会让对跖点算出 1 + 1e-16，
  // 那时 asin 会返回 NaN —— 夹住它，而不是让一个"最远的两点"变成 NaN。
  return 2 * EARTH_MEAN_RADIUS_M * Math.asin(clampUnit(Math.sqrt(clampUnit(h))));
}

/** 折线（含航点序列）的总长度，单位米。少于 2 个点时返回 0。 */
export function polylineLengthM(points: readonly LatLon[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    if (prev === undefined || curr === undefined) continue;
    total += haversineDistanceM(prev, curr);
  }
  return total;
}

/* ───────────────────────────── 方位 ───────────────────────────── */

/**
 * 从 `from` 指向 `to` 的**大圆起始方位角**（度，正北 0，顺时针 0–360）。
 *
 * 退化情形：两点重合时方向在数学上无定义，这里返回 `0`（正北）——
 * 返回 `NaN` 会让所有"算出方位角再比较"的调用方全部污染成 NaN，
 * 而"重合"这件事调用方通常能用 `haversineDistanceM === 0` 自己判断。
 */
export function initialBearingDeg(from: LatLon, to: LatLon): number {
  if (!isFiniteLatLon(from) || !isFiniteLatLon(to)) return NaN;
  const lat1 = (from.latDeg * Math.PI) / 180;
  const lat2 = (to.latDeg * Math.PI) / 180;
  const dLon = (wrapDeltaDeg(to.lonDeg - from.lonDeg) * Math.PI) / 180;

  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  const bearing = (Math.atan2(y, x) * 180) / Math.PI;
  return bearing < 0 ? bearing + 360 : bearing;
}

/* ─────────────────── 沿大圆的插值 / 推算 ─────────────────── */

/** 单位球面上的点（用于 slerp，避免在纬度上反复做三角展开）。 */
interface UnitVector {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

function toUnitVector(p: LatLon): UnitVector {
  const lat = (p.latDeg * Math.PI) / 180;
  const lon = (p.lonDeg * Math.PI) / 180;
  const cosLat = Math.cos(lat);
  return { x: cosLat * Math.cos(lon), y: cosLat * Math.sin(lon), z: Math.sin(lat) };
}

function fromUnitVector(v: UnitVector): LatLon {
  const latDeg = (Math.asin(clampUnit(v.z)) * 180) / Math.PI;
  const lonDeg = (Math.atan2(v.y, v.x) * 180) / Math.PI;
  return { latDeg, lonDeg: ((lonDeg % 360) + 540) % 360 - 180 };
}

/**
 * 沿 `from` → `to` 的大圆插值。`t = 0` 给 `from`，`t = 1` 给 `to`，
 * `t` 超出 [0, 1] 会继续沿大圆外推（航线延长线是常见需求，不必绕路）。
 *
 * 为什么用 slerp 而不是"对经纬度做线性插值"：后者在经度上是直的，
 * 在高纬度会画出明显偏离大圆的弧 —— 3000 m 的测线在高纬 60° 上能差出几十米，
 * 直接体现为航线间距不均。
 *
 * 退化情形：两点重合（或极近，夹角 < 1e-12 rad）时无法定出唯一的大圆，
 * 直接返回 `from` 的副本 —— 这是唯一合理的选择。
 */
export function interpolateGreatCircle(from: LatLon, to: LatLon, t: number): LatLon {
  if (!isFiniteLatLon(from) || !isFiniteLatLon(to) || !Number.isFinite(t)) {
    return { latDeg: NaN, lonDeg: NaN };
  }
  const v1 = toUnitVector(from);
  const v2 = toUnitVector(to);
  const dot = clampUnit(v1.x * v2.x + v1.y * v2.y + v1.z * v2.z);
  const omega = Math.acos(dot);
  if (omega < 1e-12) return { ...from };

  const sinOmega = Math.sin(omega);
  const a = Math.sin((1 - t) * omega) / sinOmega;
  const b = Math.sin(t * omega) / sinOmega;
  return fromUnitVector({
    x: a * v1.x + b * v2.x,
    y: a * v1.y + b * v2.y,
    z: a * v1.z + b * v2.z,
  });
}

/** 大圆中点。等价于 `interpolateGreatCircle(a, b, 0.5)`。 */
export function greatCircleMidpoint(a: LatLon, b: LatLon): LatLon {
  return interpolateGreatCircle(a, b, 0.5);
}

/**
 * 从 `start` 出发，按给定初始方位角走 `distanceM` 后的位置。
 *
 * 这是"沿大圆外推"的另一种形式：方位角固定、距离固定 → 终点唯一。
 * 注意走远之后大圆会弯曲（起始方位角不再等于终点方位角），
 * 这是正确的球面行为，不是 bug。
 */
export function destinationPoint(start: LatLon, bearingDeg: number, distanceM: number): LatLon {
  if (!isFiniteLatLon(start) || !Number.isFinite(bearingDeg) || !Number.isFinite(distanceM)) {
    return { latDeg: NaN, lonDeg: NaN };
  }
  const delta = distanceM / EARTH_MEAN_RADIUS_M;
  const lat1 = (start.latDeg * Math.PI) / 180;
  const lon1 = (start.lonDeg * Math.PI) / 180;
  const theta = (bearingDeg * Math.PI) / 180;

  const sinLat2 = Math.sin(lat1) * Math.cos(delta) + Math.cos(lat1) * Math.sin(delta) * Math.cos(theta);
  const lat2 = Math.asin(clampUnit(sinLat2));
  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(theta) * Math.sin(delta) * Math.cos(lat1),
      Math.cos(delta) - Math.sin(lat1) * sinLat2,
    );

  const latDeg = (lat2 * 180) / Math.PI;
  const lonDeg = (lon2 * 180) / Math.PI;
  return { latDeg, lonDeg: ((lonDeg % 360) + 540) % 360 - 180 };
}

/* ───────────────────────── 多边形 ───────────────────────── */

/**
 * 多边形的**球面**面积，单位平方米。顶点按顺序（顺/逆时针都可以）给出，
 * 首尾重复（闭合环）也可以 —— 那条零长度边的贡献是 0。
 *
 * 公式是球面过剩量的标准形式：`A = R²/2 · |Σ (λ₂-λ₁)(2 + sinφ₁ + sinφ₂)|`。
 * 经度差先规整到 (-180, 180]，这样跨 180° 经线的多边形也能算对。
 *
 * 退化情形：少于 3 个顶点 → 0（不是 NaN）；自相交多边形会得到一个无意义但
 * 有限的值 —— 球面公式对自相交没有定义，调用方应先用围栏校验保证简单多边形。
 */
export function polygonAreaM2(polygon: readonly LatLon[]): number {
  if (polygon.length < 3) return 0;
  let total = 0;
  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    if (a === undefined || b === undefined) continue;
    if (!isFiniteLatLon(a) || !isFiniteLatLon(b)) return NaN;
    const dLonRad = (wrapDeltaDeg(b.lonDeg - a.lonDeg) * Math.PI) / 180;
    const sinA = Math.sin((a.latDeg * Math.PI) / 180);
    const sinB = Math.sin((b.latDeg * Math.PI) / 180);
    total += dLonRad * (2 + sinA + sinB);
  }
  return Math.abs(total) * EARTH_MEAN_RADIUS_M * EARTH_MEAN_RADIUS_M * 0.5;
}

/** 多边形周长，单位米。首尾重复的闭合环不会把那条零长度边算两次。 */
export function polygonPerimeterM(polygon: readonly LatLon[]): number {
  const first = polygon[0];
  if (polygon.length < 2 || first === undefined) return 0;
  return polylineLengthM([...polygon, first]);
}

/**
 * 顶点经纬度的算术平均 —— 只用于给局部投影挑一个原点，不做精确质心。
 *
 * 经度不直接求平均，而是先算"相对第一个顶点的偏移"再平均：
 * 直接平均会让跨 180° 经线的环（顶点在 +179 与 -179 附近）得到一个
 * 位于 0° 经线上的"中心"，把整个投影平面甩到地球另一边。
 */
export function vertexMean(points: readonly LatLon[]): LatLon | undefined {
  const lonReference = points[0]?.lonDeg;
  if (lonReference === undefined) return undefined;

  let latSum = 0;
  let lonOffsetSum = 0;
  let count = 0;
  for (const p of points) {
    if (!isFiniteLatLon(p)) continue;
    latSum += p.latDeg;
    lonOffsetSum += wrapDeltaDeg(p.lonDeg - lonReference);
    count += 1;
  }
  if (count === 0) return undefined;

  return {
    latDeg: latSum / count,
    lonDeg: ((lonReference + lonOffsetSum / count) % 360 + 540) % 360 - 180,
  };
}

/**
 * 经纬度包围盒。
 *
 * 跨 180° 经线时返回"窄区间"表示（`westDeg > eastDeg`），详见 {@link GeoBounds}。
 * 判定方式是朴素跨度是否超过 180°：对于任何一个正常尺寸的围栏/地块，
 * 真实的经度跨度都不会超过 180°，所以这条判据在实践中是可靠的；
 * 但一个**刻意**跨越半球级的多边形会被误判，那种形状不属于本项目的使用场景。
 *
 * 空数组或全部非法 → 返回 `undefined`。
 */
export function boundsOf(points: readonly LatLon[]): GeoBounds | undefined {
  let south = Infinity;
  let north = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;
  let seen = false;
  for (const p of points) {
    if (!isFiniteLatLon(p)) continue;
    seen = true;
    if (p.latDeg < south) south = p.latDeg;
    if (p.latDeg > north) north = p.latDeg;
    // 经度先规整到 [0, 360) 再做朴素 min/max，这样跨经线的区间是"连续的一段"
    const lon = ((p.lonDeg % 360) + 360) % 360;
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
  }
  if (!seen) return undefined;

  // 先判断哪一段才是"窄区间"。在 [0, 360) 空间里，如果朴素跨度超过 180°，
  // 说明窄区间是从 maxLon 向东绕回 180° 再接到 minLon。
  const narrowWest360 = maxLon - minLon > 180 ? maxLon : minLon;
  const narrowEast360 = maxLon - minLon > 180 ? minLon + 360 : maxLon;

  const toSigned = (lon360: number): number => (((lon360 + 180) % 360) + 360) % 360 - 180;
  const westDeg = toSigned(narrowWest360);
  const eastDeg = toSigned(narrowEast360);

  return {
    southDeg: south,
    northDeg: north,
    westDeg,
    eastDeg,
    // 在 ±180 表示法里"跨经线"就等于西边界数值上大于东边界
    crossesAntimeridian: westDeg > eastDeg,
  };
}

/* ─────────────────── 局部平面投影（小范围） ─────────────────── */

/** 局部等距圆柱投影，见 {@link localPlaneAt}。 */
export interface LocalPlane {
  readonly origin: LatLon;
  toLocal(point: LatLon): LocalPoint;
  toLatLon(point: LocalPoint): LatLon;
}

/**
 * 以 `origin` 为原点建一个局部平面坐标系（东为 +x，北为 +y，单位米）。
 *
 * 用的是等距圆柱（equirectangular）近似：纬度方向 1 度 = R·π/180，
 * 经度方向 1 度 = R·π/180·cos(φ₀)，用**原点纬度**作为缩放因子。
 *
 * 误差：在离原点 10 km 处，长度误差 < 0.1%（< 10 m）；100 km 处约 1%。
 * 所以它只适合"绕一个中心生成航线"这类用途，不适合跨区域批量转换。
 *
 * 极区退化：`origin.latDeg` 接近 ±90 时 `cos(φ₀) → 0`，经度方向的缩放因子
 * 会爆掉。这里把 `cos(φ₀)` 夹在 1e-6（约 0.06 m 每度经度），
 * 结果是投影在极点附近**变得非常粗糙但仍然是有限值** ——
 * 比返回 Infinity/NaN 让整条航线变 NaN 要好，而且极区本来就不在这些
 * 生成器的适用范围内。
 */
export function localPlaneAt(origin: LatLon): LocalPlane {
  const lat0Rad = (origin.latDeg * Math.PI) / 180;
  const cosLat0 = Math.max(Math.abs(Math.cos(lat0Rad)), 1e-6);
  const mPerDegLat = (EARTH_MEAN_RADIUS_M * Math.PI) / 180;
  const mPerDegLon = mPerDegLat * cosLat0;

  return {
    origin,
    toLocal(point: LatLon): LocalPoint {
      return {
        eastM: wrapDeltaDeg(point.lonDeg - origin.lonDeg) * mPerDegLon,
        northM: (point.latDeg - origin.latDeg) * mPerDegLat,
      };
    },
    toLatLon(point: LocalPoint): LatLon {
      const latDeg = origin.latDeg + point.northM / mPerDegLat;
      const lonDeg = origin.lonDeg + point.eastM / mPerDegLon;
      return { latDeg, lonDeg: ((lonDeg % 360) + 540) % 360 - 180 };
    },
  };
}

/**
 * 用原点 + 宽/高 + 方位角构造一个矩形（四个顶点，逆时针，不重复首点）。
 *
 * 用途：测绘任务的"框选地块"。`widthM` 是**垂直于** `bearingDeg` 的边长
 * （跨测线方向），`heightM` 是**沿** `bearingDeg` 的边长（沿测线方向）。
 * 这个约定与 {@link surveyLines} 的 `trackBearingDeg` 配套：
 * `widthM` 决定有多少条测线，`heightM` 决定每条测线多长。
 */
export function rectanglePolygon(
  center: LatLon,
  widthM: number,
  heightM: number,
  bearingDeg: number,
): readonly LatLon[] {
  const plane = localPlaneAt(center);
  const theta = (bearingDeg * Math.PI) / 180;
  const cosT = Math.cos(theta);
  const sinT = Math.sin(theta);
  const halfW = widthM / 2;
  const halfH = heightM / 2;
  // 角点在局部平面里先按"正北朝上的矩形"算，再整体旋转 bearingDeg
  const corners: readonly (readonly [number, number])[] = [
    [-halfW, -halfH],
    [halfW, -halfH],
    [halfW, halfH],
    [-halfW, halfH],
  ];
  return corners.map(([u, v]) => {
    const eastM = u * cosT + v * sinT;
    const northM = -u * sinT + v * cosT;
    return plane.toLatLon({ eastM, northM });
  });
}

/* ───────────────── 点与多边形的关系（围栏判定） ───────────────── */

/** 点相对多边形的位置。 */
export type PointInPolygonResult = 'inside' | 'outside' | 'boundary';

/**
 * 判断点相对多边形的位置，落在（或极靠近）边界上时返回 `'boundary'`。
 *
 * 做法：以**多边形的顶点均值**为原点建局部平面，把多边形顶点与查询点都投影
 * 过去，再用水平射线法（ray casting）数穿越次数。
 *
 * 为什么原点取多边形的中心、而不是查询点本身：投影里的经度差要按 ±180° 规整，
 * 规整的"接缝"落在离原点 180° 处。若把原点放在查询点上，一个跨 180° 经线的
 * 围栏在经度 0 附近的查询点看来会**撕裂成包围整个地球的大四边形** —— 实测中
 * 它会把"地球另一面"的点判成 `inside`，这是围栏判定里最危险的一类错误。
 * 原点放在多边形中心时，围栏自身的顶点永远是连续的一段，接缝被推到
 * 离围栏 180° 的"地球另一面"，而那里的结论本来就无意义（见下）。
 *
 * `edgeToleranceM > 0` 时，到任一边的距离在该容差内的点也算 `'boundary'`。
 * 围栏判定通常需要这个：GPS 噪声 ±3 m，正好压在线上的点每秒都可能翻转结论，
 * 半透明提示"接近边界"比来回打"闯入/离开"事件友好得多。
 *
 * 边界条件的处理：
 * - 少于 3 个顶点 → `'outside'`（退化多边形围不出任何区域）。
 * - 顶点重复（零长度边）→ 该边在射线法里被忽略，不产生伪穿越。
 * - 跨 180° 经线 → 经度差按 ±180 规整，正常处理。
 * - 极区 → 与 {@link localPlaneAt} 同样退化，返回有限但粗糙的结果。
 * - **查询点恰好落在离多边形中心 180° 的接缝上**（即地球另一面）→ 结论无定义。
 *   这是任何平面投影都躲不掉的，不是本实现的缺陷；任何 2D 判据在
 *   "地球另一面"这种输入下都只能给出一个约定值。
 * - 边界判定用 1 微米（局部平面坐标）的容差代替 `=== 0`：
 *   围栏顶点与航线端点来自同一套投影，浮点往返后可能差 1e-10 m，
 *   若要求严格等于 0，这些"明明在边界上"的点会落进射线法的任意分支。
 */
export function classifyPointInPolygon(
  point: LatLon,
  polygon: readonly LatLon[],
  edgeToleranceM = 0,
): PointInPolygonResult {
  if (polygon.length < 3 || !isFiniteLatLon(point)) return 'outside';
  for (const vertex of polygon) {
    if (!isFiniteLatLon(vertex)) return 'outside';
  }

  const center = vertexMean(polygon);
  if (center === undefined) return 'outside';

  const plane = localPlaneAt(center);
  const query = plane.toLocal(point);

  const xs: number[] = [];
  const ys: number[] = [];
  for (const vertex of polygon) {
    const local = plane.toLocal(vertex);
    xs.push(local.eastM);
    ys.push(local.northM);
  }

  const boundaryEpsilonM = 1e-6;
  const toleranceM = Math.max(edgeToleranceM, boundaryEpsilonM);
  for (let i = 0; i < xs.length; i += 1) {
    const j = (i + 1) % xs.length;
    const ax = xs[i];
    const ay = ys[i];
    const bx = xs[j];
    const by = ys[j];
    if (ax === undefined || ay === undefined || bx === undefined || by === undefined) continue;
    if (distancePointToSegmentM(query.eastM, query.northM, ax, ay, bx, by) <= toleranceM) {
      return 'boundary';
    }
  }

  let inside = false;
  for (let i = 0; i < xs.length; i += 1) {
    const j = (i + 1) % xs.length;
    const xi = xs[i];
    const yi = ys[i];
    const xj = xs[j];
    const yj = ys[j];
    if (xi === undefined || yi === undefined || xj === undefined || yj === undefined) continue;
    // 半开区间（`>` 不含下界）保证顶点正好落在射线上时只算一次穿越
    if ((yi > query.northM) !== (yj > query.northM)) {
      const xCross = xi + ((query.northM - yi) / (yj - yi)) * (xj - xi);
      if (xCross > query.eastM) inside = !inside;
    }
  }

  return inside ? 'inside' : 'outside';
}

/**
 * 点是否在多边形内。**边界上算在内**。
 *
 * 这个默认值是按围栏语义挑的：ArduPilot 的围栏把"贴边飞行"当作仍在围栏内，
 * 只有明确出界才触发动作。需要"边界即违规"的场景请直接用
 * {@link classifyPointInPolygon} 并要求结果是 `'outside'`。
 */
export function pointInPolygon(point: LatLon, polygon: readonly LatLon[]): boolean {
  return classifyPointInPolygon(point, polygon) !== 'outside';
}

/** 点到线段的最短距离（平面坐标，单位与输入一致）。用于容差判定。 */
function distancePointToSegmentM(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) {
    // 零长度线段（重复顶点）：退化为点到点距离
    return Math.hypot(px - ax, py - ay);
  }
  const t = ((px - ax) * dx + (py - ay) * dy) / lengthSq;
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(px - (ax + clamped * dx), py - (ay + clamped * dy));
}

/* ─────────────── 绕中心生成航点：同心环 / 螺旋 ─────────────── */

/** 绕中心生成航点的公共选项。 */
export interface RingPatternOptions {
  readonly center: LatLon;
  /** 最内圈半径（米）。0 表示从中心开始。 */
  readonly startRadiusM: number;
  /** 相邻两圈之间的径向间距（米），必须大于 0。 */
  readonly ringSpacingM: number;
  /** 沿航迹相邻航点的间距（米），必须大于 0。 */
  readonly waypointSpacingM: number;
  /** true 为顺时针（俯视，北朝上）。默认 true。 */
  readonly clockwise?: boolean;
}

/** 同心环：`ringCount` 圈，每圈的航点按 `waypointSpacingM` 均匀铺开。 */
export interface ConcentricRingsOptions extends RingPatternOptions {
  /** 圈数，必须 >= 1。 */
  readonly ringCount: number;
}

/**
 * 生成同心环航点（一圈一个圆弧，逐圈向外或向内）。
 *
 * 每圈的航点数按 **周长 / `waypointSpacingM`** 计算并取整（至少 3 个 ——
 * 两个点围不出一个环）。这意味着**半径越大，该圈的航点越多**，
 * 从而沿航迹的间距在所有圈上都接近 `waypointSpacingM`。
 * 这正是"等距航点"的含义：不是每圈固定点数（那样外圈会明显变稀疏），
 * 而是让相邻航点的**弧长**一致。
 *
 * 退化情形：`ringSpacingM <= 0`、`waypointSpacingM <= 0`、`ringCount < 1`
 * 或中心非法 → 返回 `[]`。返回空数组而不是抛异常，是因为调用方是界面：
 * 用户把间距填成 0 时应该看到"无法生成航线"，而不是整个页面崩掉。
 */
export function concentricRings(options: ConcentricRingsOptions): readonly LatLon[] {
  const { center, startRadiusM, ringSpacingM, waypointSpacingM, ringCount, clockwise = true } = options;
  if (!isFiniteLatLon(center)) return [];
  if (!(ringSpacingM > 0) || !(waypointSpacingM > 0) || ringCount < 1) return [];
  if (!Number.isFinite(startRadiusM)) return [];

  const plane = localPlaneAt(center);
  const direction = clockwise ? 1 : -1;
  const points: LatLon[] = [];

  for (let ring = 0; ring < ringCount; ring += 1) {
    const radiusM = startRadiusM + ring * ringSpacingM;
    const count = Math.max(3, Math.round((2 * Math.PI * radiusM) / waypointSpacingM));
    for (let i = 0; i < count; i += 1) {
      // 从正北开始，按 direction 绕行
      const angle = (direction * 2 * Math.PI * i) / count;
      points.push(
        plane.toLatLon({
          eastM: radiusM * Math.sin(angle),
          northM: radiusM * Math.cos(angle),
        }),
      );
    }
  }

  return points;
}

/** 阿基米德螺旋：半径随角度线性增长，每圈外扩 `ringSpacingM`。 */
export interface SpiralOptions extends RingPatternOptions {
  /** 最外圈半径（米），必须 >= `startRadiusM`。 */
  readonly endRadiusM: number;
}

/**
 * 生成阿基米德螺旋航点：`r(θ) = startRadius + (ringSpacingM / 2π) · θ`。
 *
 * 航点按**沿航迹的距离**等距铺设（相邻两点的路径长度 ≈ `waypointSpacingM`），
 * 而不是按角度等分。差别很大：按角度等分时，外圈同样的角度间隔对应更长的
 * 路径长度，螺旋外段会明显变稀。
 *
 * 实现上把 θ 细分成 4096 小步，逐段累加小弦长 `|P(θ_{i+1}) − P(θ_i)|`
 * （在这么小的步长下与弧长等价），每走够 `waypointSpacingM` 落一个航点。
 *
 * **起点在圆心的注意点**：`startRadiusM = 0` 时第一段的**直线**距离会明显
 * 小于 `waypointSpacingM`（约等于它的一半）—— 路径已经从圆心走了 50 m，
 * 但此时曲率最大、位移只有约 25 m。这是螺旋的几何事实，不是间距算错；
 * 真实规划应从 `startRadiusM > 0` 开始，那时相邻航点的直线距离与请求值
 * 相差不到 1%。
 *
 * 步长选择的代价：4096 步意味着实际间距最多比 `waypointSpacingM` 多一个步长
 * （在几十圈的螺旋上是 0.1% 量级）。
 *
 * 退化情形与 {@link concentricRings} 相同：非法输入返回 `[]`。
 * `endRadiusM < startRadiusM` 也返回 `[]`（负圈数是调用方的错误，不该静默变成向内螺旋）。
 */
export function spiralWaypoints(options: SpiralOptions): readonly LatLon[] {
  const {
    center,
    startRadiusM,
    endRadiusM,
    ringSpacingM,
    waypointSpacingM,
    clockwise = true,
  } = options;
  if (!isFiniteLatLon(center)) return [];
  if (!(ringSpacingM > 0) || !(waypointSpacingM > 0)) return [];
  if (!Number.isFinite(startRadiusM) || !Number.isFinite(endRadiusM)) return [];
  if (endRadiusM < startRadiusM) return [];

  const plane = localPlaneAt(center);
  const direction = clockwise ? 1 : -1;
  const drdTheta = ringSpacingM / (2 * Math.PI);
  const totalTheta = (endRadiusM - startRadiusM) / drdTheta;

  const localAt = (theta: number): LocalPoint => {
    const radiusM = startRadiusM + drdTheta * theta;
    const angle = direction * theta;
    return { eastM: radiusM * Math.sin(angle), northM: radiusM * Math.cos(angle) };
  };

  if (totalTheta <= 0) {
    // 起止半径相同：退化成一个圆环
    const radiusM = startRadiusM;
    const count = Math.max(3, Math.round((2 * Math.PI * radiusM) / waypointSpacingM));
    const ring: LatLon[] = [];
    for (let i = 0; i < count; i += 1) {
      const angle = (direction * 2 * Math.PI * i) / count;
      ring.push(plane.toLatLon({ eastM: radiusM * Math.sin(angle), northM: radiusM * Math.cos(angle) }));
    }
    return ring;
  }

  const steps = 4096;
  const thetaStep = totalTheta / steps;
  const points: LatLon[] = [];
  points.push(plane.toLatLon(localAt(0)));

  let previous = localAt(0);
  let sinceLastEmitM = 0;
  for (let step = 1; step <= steps; step += 1) {
    const current = localAt(step * thetaStep);
    sinceLastEmitM += Math.hypot(current.eastM - previous.eastM, current.northM - previous.northM);
    previous = current;
    if (sinceLastEmitM >= waypointSpacingM) {
      points.push(plane.toLatLon(current));
      sinceLastEmitM = 0;
    }
  }

  return points;
}

/* ─────────────────── 测绘航线：扫描线 ─────────────────── */

/** 一条测绘扫描线（一条直测线）。 */
export interface SurveyLine {
  readonly start: LatLon;
  readonly end: LatLon;
  readonly lengthM: number;
}

export interface SurveyLineOptions {
  /** 地块多边形（>= 3 个顶点，简单多边形，按顺序给出）。 */
  readonly polygon: readonly LatLon[];
  /** 相邻测线间距（米），必须大于 0。 */
  readonly spacingM: number;
  /** 测线方向（度，正北 0、顺时针）。测绘惯例是取地块长边方向以减少转弯。 */
  readonly trackBearingDeg: number;
}

/**
 * 在给定多边形内生成平行扫描线（测绘航线）。
 *
 * 做法：把多边形投影到以顶点均值为原点的局部平面，再旋转到"沿测线"与
 * "跨测线"两个方向上：
 *
 * - `s`（沿测线）= `east·sinθ + north·cosθ`，即投影到测线方位角方向上的距离；
 * - `t`（跨测线）= `east·cosθ − north·sinθ`，即垂直于测线的偏移。
 *
 * 一条"平行于测线"的直线就是 `t = 常数`。对每个 `t` 求它与多边形各边的
 * `s` 交点，排序后两两配对得到线段，最后转回经纬度。
 *
 * 线数与位置：`n = max(1, ceil(跨测线跨度 / spacingM))`，实际步长
 * `= 跨度 / n`（**不大于**请求间距，见下），每条线放在自己那一份的**中间**
 * （`tMin + step·(k + 0.5)`）。直接按 `spacingM` 从一侧排下去的话，最后一档会
 * 剩一条宽度不等的边，且末条线可能贴着边界（一半在地块外）；
 * 居中分布让所有间距均匀，且每条线都真正落在多边形内部。
 * 实际步长与请求值的差异通过 {@link SurveyPlan.effectiveSpacingM} 暴露给调用方。
 *
 * 输出顺序是**蛇形（boustrophedon）**：奇数行反向，使相邻两行的终点与
 * 起点相邻。直接把这些点当航线走，空驶距离最短。调用方若需要
 * "每条线都同向"，自己反转奇数行即可。
 *
 * 位姿误差：投影是等距圆柱近似（见 {@link localPlaneAt}），
 * 在 10 km 尺度上测线间距的误差 < 0.1%（1 m 间距上 < 1 mm）。
 *
 * 退化情形：顶点少于 3 个、`spacingM <= 0`、跨测线跨度为 0（所有点共线或
 * 重合）、或方向角非法 → 返回 `[]`。
 */
export function surveyLines(options: SurveyLineOptions): readonly SurveyLine[] {
  return computeSurvey(options).lines;
}

interface ComputedSurvey {
  readonly lines: readonly SurveyLine[];
  /** 实际生效的网格步长（`ceil` 取整后**不大于**请求间距）。 */
  readonly stepM: number;
}

function computeSurvey(options: SurveyLineOptions): ComputedSurvey {
  const empty: ComputedSurvey = { lines: [], stepM: 0 };
  const { polygon, spacingM, trackBearingDeg } = options;
  if (polygon.length < 3) return empty;
  if (!(spacingM > 0) || !Number.isFinite(spacingM)) return empty;
  if (!Number.isFinite(trackBearingDeg)) return empty;
  for (const vertex of polygon) {
    if (!isFiniteLatLon(vertex)) return empty;
  }

  const center = vertexMean(polygon);
  if (center === undefined) return empty;

  const plane = localPlaneAt(center);
  const theta = (trackBearingDeg * Math.PI) / 180;
  const cosT = Math.cos(theta);
  const sinT = Math.sin(theta);

  const alongTrack: number[] = [];
  const crossTrack: number[] = [];
  for (const vertex of polygon) {
    const local = plane.toLocal(vertex);
    alongTrack.push(local.eastM * sinT + local.northM * cosT);
    crossTrack.push(local.eastM * cosT - local.northM * sinT);
  }

  let tMin = Infinity;
  let tMax = -Infinity;
  for (const t of crossTrack) {
    if (t < tMin) tMin = t;
    if (t > tMax) tMax = t;
  }
  const span = tMax - tMin;
  if (!(span > 0) || !Number.isFinite(span)) return empty;

  // 线数用 ceil 而不是 floor：测绘要求"实际间距不大于请求间距"，
  // 否则地块两侧会各留下一条比请求更宽的漏拍带。
  // 减去的相对 epsilon 是为了抵消浮点噪声 —— 跨度明明等于 4.000 倍间距时，
  // 算出来可能是 4.0000000001，floor/ceil 都会给出令人意外的结果
  // （实测中就出现过"400 m 宽、100 m 间距"只生成 3 条线）。
  const adjustedSpan = span - span * 1e-9;
  const lineCount = Math.max(1, Math.ceil(adjustedSpan / spacingM));
  const step = span / lineCount;

  const lines: SurveyLine[] = [];
  for (let k = 0; k < lineCount; k += 1) {
    const t = tMin + step * (k + 0.5);

    const crossings: number[] = [];
    for (let i = 0; i < crossTrack.length; i += 1) {
      const j = (i + 1) % crossTrack.length;
      const ti = crossTrack[i];
      const si = alongTrack[i];
      const tj = crossTrack[j];
      const sj = alongTrack[j];
      if (ti === undefined || si === undefined || tj === undefined || sj === undefined) continue;
      // 半开区间（`<=` 不含上界）保证顶点正好落在扫描线上时不会算两次
      if ((ti <= t) !== (tj <= t)) {
        const ratio = (t - ti) / (tj - ti);
        crossings.push(si + ratio * (sj - si));
      }
    }
    if (crossings.length < 2) continue;
    crossings.sort((a, b) => a - b);

    // 先按 s 升序收集这一行的所有线段；自相交多边形会给出奇数个交点，
    // 落单的那个在配对时被后面的 undefined 检查挡掉。
    const rowSegments: Array<{ readonly sStart: number; readonly sEnd: number }> = [];
    for (let index = 0; index + 1 < crossings.length; index += 2) {
      const sLow = crossings[index];
      const sHigh = crossings[index + 1];
      if (sLow === undefined || sHigh === undefined) continue;
      if (!(sHigh - sLow > 0)) continue;
      rowSegments.push({ sStart: sLow, sEnd: sHigh });
    }

    // 蛇形：奇数行整体反向，且行内每段也反向。只反向段间顺序是不够的 ——
    // 凹多边形的一行会有多段，段内不反向的话航迹会在行内来回横跳。
    const ordered =
      k % 2 === 0
        ? rowSegments
        : rowSegments
            .slice()
            .reverse()
            .map((segment) => ({ sStart: segment.sEnd, sEnd: segment.sStart }));

    for (const segment of ordered) {
      const start = plane.toLatLon(toEastNorth(segment.sStart, t, sinT, cosT));
      const end = plane.toLatLon(toEastNorth(segment.sEnd, t, sinT, cosT));
      lines.push({ start, end, lengthM: haversineDistanceM(start, end) });
    }
  }

  return { lines, stepM: step };
}

/**
 * 局部旋转坐标 → (east, north)。
 * `s` 是沿测线的距离，`t` 是跨测线的偏移（`toEastNorth` 的逆变换）。
 */
function toEastNorth(s: number, t: number, sinT: number, cosT: number): LocalPoint {
  return { eastM: s * sinT + t * cosT, northM: s * cosT - t * sinT };
}

/**
 * 把 {@link surveyLines} 的输出拍成一条连续航点序列。
 *
 * 因为 `surveyLines` 已经是蛇形序，这里只需要按顺序取每个端点的起/终点 ——
 * 相邻两条线的终点与起点本身就在地块的两端相邻，不必再插过渡点。
 */
export function surveyPath(lines: readonly SurveyLine[]): readonly LatLon[] {
  const path: LatLon[] = [];
  for (const line of lines) {
    path.push(line.start, line.end);
  }
  return path;
}

/** 测绘航线的汇总信息，供界面显示"多少条线、总长多少"这类提示。 */
export interface SurveyPlan {
  readonly lines: readonly SurveyLine[];
  readonly path: readonly LatLon[];
  readonly lineCount: number;
  readonly totalLengthM: number;
  /**
   * 实际生效的**网格步长**（米），`ceil` 取整后**不大于**请求的 `spacingM`。
   *
   * 刻意暴露这个数字：请求 30 m 间距、地块跨度 400 m 时只能整分成 14 份，
   * 实际步长约 28.6 m。让界面直接显示它，比让用户拿"总长 ÷ 线数"去反推要好。
   * 只有一条线时它等于跨线跨度本身（那条线被放在跨度的正中间）。
   */
  readonly effectiveSpacingM: number;
}

/**
 * {@link surveyLines} 的便利封装：一次拿到线、拼接后的航点序列与统计信息。
 */
export function surveyPlan(options: SurveyLineOptions): SurveyPlan {
  const computed = computeSurvey(options);
  let totalLengthM = 0;
  for (const line of computed.lines) totalLengthM += line.lengthM;
  return {
    lines: computed.lines,
    path: surveyPath(computed.lines),
    lineCount: computed.lines.length,
    totalLengthM,
    effectiveSpacingM: computed.stepM,
  };
}
